import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import type { ReactNode } from 'react';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import SubtleBadge from '@/components/common/SubtleBadge';
import { intentMap, purposeMap } from './keywordGapColumnConfig';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import { difficulyRanges, serpDifficuly } from '@/helpers/serpDifficulity';
import { Table } from 'react-bootstrap';
import Divider from '@/components/common/Divider';
import AiLogo from '@/assets/img/artificial-intelligence.svg?react';
import NumberRenderer from '@/app/common/components/CellRenderers/NumberRenderer';
import { trackerTooltips } from '@/app/KeywordTracker/tooltip/trackerTooltips';
import numberHumanizer from '@/helpers/numberHumanizer';

type CompetitorsRow = {
  url: string;
  keyword: string;
  rank?: number;
  date?: string;
  difficulty?: number;
  keyword_search_volume?: number;
  intent: string[];
  purpose: string[];
  len_keyword?: number;
};

type BestPageFoundRow = {
  page_found: string;
  avg_rank?: number;
  keyword_count?: number;
  total_volume?: number;
};

type CompetitorsColumn = ColDef<CompetitorsRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  icon?: ReactNode;
  tooltip?: ReactNode;
};

type BestPageFoundColumn = ColDef<BestPageFoundRow> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  tooltip?: ReactNode;
};

export const competitorsColumnConfig: CompetitorsColumn[] = [
  {
    field: 'url',
    type: 'string',
    title: 'نشانی',
    minWidth: 180,
    flex: 1,
    tooltip: keywordResearchTooltips.columns.URL,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) => (
      <URLRenderer hideLockIcon url={params.value} />
    )
  },
  {
    field: 'keyword',
    type: 'string',
    title: 'کلمه',
    minWidth: 120,
    tooltip: keywordResearchTooltips.columns.Keyword,
    searchable: true
  },
  {
    field: 'rank',
    type: 'number',
    title: 'رتبه',
    width: 90,
    searchable: true,
    tooltip: keywordResearchTooltips.columns.rank_now,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) =>
      params.value && (
        <div className="d-flex align-items-center justify-content-center">
          <ChildrensTooltip
            delayToRemove={0}
            delayToShow={1000}
            placement="top"
            html={params.data?.date}
          >
            <span className="px-2 m-auto w-100 text-center">
              {numberHumanizer(params.value)}
            </span>
          </ChildrensTooltip>
        </div>
      )
  },
  // {
  //   field: 'date',
  //   type: 'string',
  //   title: 'تاریخ',
  //   width: 95,
  //   searchable: false,
  //   // filter: 'agTextColumnFilter',
  //   floatingFilter: false
  // },
  // {
  //   field: 'difference',
  //   type: 'number',
  //   title: 'تغییر رتبه',
  //   width: 115,
  //   searchable: true,

  //   tooltip: keywordResearchTooltips.columns.difference,
  //   cellRenderer: params => (
  //     <div className="d-flex h-100 align-items-center justify-content-around">
  //       <h6
  //         className={classNames('fs--2 mb-0', {
  //           'text-danger': params.value < 0,
  //           'text-success': params.value > 0
  //         })}
  //       >
  //         {params.value < 0 ? -1 * params.value : params.value}
  //         {params.value > 0 ? (
  //           <FontAwesomeIcon icon="caret-up" className="ms-1" />
  //         ) : params.value < 0 ? (
  //           <FontAwesomeIcon icon="caret-down" className="ms-1" />
  //         ) : (
  //           ''
  //         )}
  //       </h6>
  //     </div>
  //   )
  // },

  {
    field: 'difficulty',
    suppressHeaderMenuButton: true,
    type: 'number',
    title: 'سختی ',
    tooltip:
      'هرچه مقدار بالاتر باشد، کسب رتبه در بین ۱۰ نتیجه اول گوگل دشوارتر خواهد بود. سختی بیشتر یعنی نیاز به زمان، هزینه و تکنیک‌های پیشرفته‌تر برای رسیدن به رتبه‌های برتر.',
    width: 90,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) =>
      params.value && (
        <div className="d-flex align-items-center justify-content-center">
          <ChildrensTooltip
            placement="top"
            html={
              <div>
                سختی کلمه کلیدی
                <Divider />
                <Table borderless size="sm">
                  <tbody>
                    {difficulyRanges.map(([start, end]) => (
                      <tr
                        className={classNames({
                          'fw-black fs--1 position-relative':
                            serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color
                        })}
                        key={start}
                      >
                        <td className="d-flex  align-items-center gap-1">
                          {serpDifficuly(start).color ===
                            serpDifficuly(params.value)?.color && (
                            <FontAwesomeIcon
                              style={{
                                right: 3,
                                top: '50%',
                                transform: 'translateY(-50%)'
                              }}
                              icon="chevron-left"
                              className="position-absolute text-primary"
                              size="sm"
                            />
                          )}
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {start}
                          </span>
                          <FontAwesomeIcon icon="minus" />
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              direction: 'ltr',
                              height: '20px',
                              width: '28px',
                              fontSize: '12px',
                              backgroundColor: serpDifficuly(start).color
                            }}
                            className="fs--2 px-2 py-0.5 text-white rounded-2"
                          >
                            {end}
                          </span>
                        </td>
                        <td className="text-white">
                          {serpDifficuly(start).label}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            }
          >
            <div
              className="fs-1 rounded-2 w-full"
              style={{
                fontWeight: 'bold'
              }}
            >
              {/* {serpDifficuly(params.value).label}{' '} */}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  direction: 'ltr',
                  marginTop: '6px',
                  height: '20px',
                  width: '28px',
                  fontSize: '12px',
                  backgroundColor: serpDifficuly(params.value).color
                }}
                className="fs--2 px-2 py-0.5 text-white rounded-2"
              >
                {params.value}
              </span>
            </div>
          </ChildrensTooltip>
        </div>
      )
  },
  {
    field: 'keyword_search_volume',
    suppressHeaderMenuButton: true,
    type: 'number',
    title: 'میزان جستجو',
    width: 130,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) =>
      params.value !== 0 &&
      params.value && <NumberRenderer text={params.value} />
  },
  {
    field: 'intent',
    suppressHeaderMenuButton: true,
    type: 'select',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    title: 'نیت',
    width: 90,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value?.map((intent, i) => (
            <ChildrensTooltip
              placement="top"
              key={i}
              delayToRemove={0}
              html={intentMap[intent as keyof typeof intentMap]?.tooltip}
            >
              <SubtleBadge
                pill
                key={i}
                bg={intentMap[intent as keyof typeof intentMap]?.variant}
                className="d-flex align-items-center gap-1  fs--2"
              >
                {intent}
              </SubtleBadge>
            </ChildrensTooltip>
          ))}
        </div>
      );
    },
    tooltip: (
      <div className="text-start">
        نیت جستجو (Search Intent) یعنی قصد کاربر از جستجوی یک کلمه کلیدی چیه؟
        چهار نوع اصلی داره:
        <br /> Informational (هدف اطلاعاتی): کاربر به دنبال اطلاعات درباره یک
        موضوع خاص است. مثال: “چگونه یک ماشین دنده اتومات تعمیر کنیم؟” “فواید
        ورزش برای سلامتی.” این کلمات برای نوشتن مقالات مناسبن. <br />
        Navigational (هدف هدایت‌شده): کاربر به دنبال دسترسی به یک وب‌سایت یا
        موضوع خاص است. مثال: خرید عینک آفتابی در دیجیکالا.
        <br />
        Commercial (تحقیق تجاری): این نوع جستجو ترکیبیه و کاربر هنوز تصمیم
        نگرفته، اما در حال بررسی و مقایسه گزینه‌هاست. مثال: “بهترین گوشی‌های
        2025.” “مقایسه پژو 206 و تیبا.” <br />
        Transactional (هدف تراکنشی یا تجاری): کاربر قصد خرید یا انجام یک اقدام
        خاص را دارد. مثال: “خرید لپ‌تاپ اپل.” “رزرو هتل در کیش.” داده های ستون
        نیت از هوش مصنوعی گرفته میشه.
      </div>
    )
  },
  {
    field: 'purpose',
    suppressHeaderMenuButton: true,
    type: 'select',
    width: 90,
    title: 'هدف',
    icon: (
      <AiLogo
        style={{
          width: 13,
          marginTop: 6,
          fill: 'var(--ag-header-foreground-color)'
        }}
      />
    ),
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CompetitorsRow>) => {
      return (
        <div className="w-100 d-flex h-100 align-items-center justify-content-start gap-2">
          {params.value?.map((purpose, i) => (
            <SubtleBadge
              pill
              key={i}
              bg="info"
              className="d-flex align-items-center gap-1  fs--2"
            >
              {purposeMap[purpose]}
            </SubtleBadge>
          ))}
        </div>
      );
    },
    tooltip: (
      <>
        پیشنهاد میده که کلمه برای چه نوع محتوایی مناسب‌تره. مثلاً برای
        <strong>&nbsp;دسته‌بندی </strong>سایت، <strong>&nbsp;مقاله</strong>، یا
        <strong>&nbsp;برند</strong> مفیده. با استفاده از این فیلتر می‌تونید
        کلمات کلیدی رو بر اساس هدف خاص خودتون (مثل ایجاد محتوای آموزشی، تبلیغاتی
        یا برندینگ) انتخاب کنید. داده های این بخش از هوش مصنوعی گرفته میشه.
      </>
    )
  },

  {
    field: 'len_keyword',
    type: 'number',
    tooltip: keywordResearchTooltips.columns.len_keyword,
    title: 'طول',
    width: 90,
    searchable: true
  }
];

export const bestPageFoundColumnConfig: BestPageFoundColumn[] = [
  {
    field: 'page_found',
    type: 'string',
    flex: 1,
    title: 'صفحات برتر در گوگل',
    minWidth: 350,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<BestPageFoundRow>) => (
      <URLRenderer url={params.value} />
    )
  },
  {
    field: 'avg_rank',
    type: 'number',
    title: 'رتبه میانگین',
    width: 120,
    searchable: true,
    // valueGetter: params => {
    //   console.log(params);
    //   return params.data?.avg_rank?.toFixed(2);
    // },
    tooltip: trackerTooltips.mainGrid.columns.average_rank
  },
  {
    searchable: true,
    field: 'keyword_count',
    type: 'number',
    title: 'تعداد کلمات',
    width: 120
  },
  {
    field: 'total_volume',
    type: 'number',
    title: 'میزان جستجو',
    width: 130,
    searchable: true
  }
];
